import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'
import { useEffect } from 'react'
import { Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon } from 'lucide-react'

interface RichEditorProps {
  value: string
  onChange: (markdown: string) => void
  placeholder?: string
}

export function RichEditor({ value, onChange, placeholder }: RichEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ HTMLAttributes: { class: 'rounded-md' } }),
      Placeholder.configure({ placeholder: placeholder ?? 'Start writing…' }),
      Markdown.configure({
        html: false,
        breaks: true,
        linkify: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      // tiptap-markdown attaches `editor.storage.markdown.getMarkdown()`
      const md = (editor.storage as unknown as Record<string, { getMarkdown?: () => string }>).markdown
        ?.getMarkdown?.()
      onChange(md ?? editor.getHTML())
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-marrow max-w-none min-h-[400px] focus:outline-none prose-headings:font-serif prose-headings:text-marrow-900 prose-p:text-marrow-800 dark:prose-headings:text-bone-50 dark:prose-p:text-bone-200',
      },
    },
  })

  // Update editor when external `value` changes (e.g. loading existing
  // newsletter into the form).
  useEffect(() => {
    if (!editor) return
    const current = (editor.storage as unknown as Record<string, { getMarkdown?: () => string }>).markdown
      ?.getMarkdown?.()
    if (current !== value) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value])

  if (!editor) return null

  return (
    <div className="rounded-2xl border border-bone-200 bg-white dark:border-marrow-700 dark:bg-marrow-800">
      <div className="flex flex-wrap items-center gap-1 border-b border-bone-200 px-3 py-2 dark:border-marrow-700">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}><Bold size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}><Italic size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')}><Strikethrough size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })}><Heading2 size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })}><Heading3 size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}><List size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}><ListOrdered size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')}><Quote size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton
          onClick={() => {
            const url = window.prompt('Link URL')
            if (!url) return
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
          }}
          active={editor.isActive('link')}
        >
          <LinkIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            const url = window.prompt('Image URL')
            if (!url) return
            editor.chain().focus().setImage({ src: url }).run()
          }}
          active={false}
        >
          <ImageIcon size={16} />
        </ToolbarButton>
      </div>
      <div className="px-4 py-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

function ToolbarButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-md p-1.5 transition',
        active
          ? 'bg-rust-500/15 text-rust-700 dark:text-rust-500'
          : 'text-marrow-800/70 hover:bg-bone-100 dark:text-bone-200/70 dark:hover:bg-marrow-700',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-bone-200 dark:bg-marrow-700" aria-hidden />
}
